import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CreditCard, CalendarX, KeyRound, ArrowRight, ArrowDown, Workflow, X } from "lucide-react";

const FLYER_URL = "/flyer-casa-18.png";

const PILLARS = [
  { icon: CreditCard, title: "Sin crédito", subtitle: "Sin deudas" },
  { icon: CalendarX, title: "Sin cuotas", subtitle: "Sin ataduras" },
  { icon: KeyRound, title: "Tu casa", subtitle: "Es tuya" },
];

export const FlagshipPromise = () => {
  const [showSystem, setShowSystem] = useState(false);
  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  React.useEffect(() => {
    const handler = () => setShowSystem(true);
    window.addEventListener("open-sistema-modal", handler);
    return () => window.removeEventListener("open-sistema-modal", handler);
  }, []);

  return (
    <section
      id="propuesta-estrella"
      className="relative min-h-screen w-full overflow-hidden bg-[#0A192F]"
      data-testid="flagship-section"
    >
      {/* Decorative accents */}
      <div aria-hidden className="absolute inset-0 grid-lines opacity-20" />
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-[36rem] h-[36rem] bg-[#F5B400]/10 blur-3xl rounded-full"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-40 w-[36rem] h-[36rem] bg-[#2563EB]/20 blur-3xl rounded-full"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-32 pb-20 min-h-screen flex flex-col justify-center">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* LEFT: Big headline + pillars */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-7"
            >
              <span className="h-px w-12 bg-[#F5B400]" />
              <span className="font-headings text-xs sm:text-sm tracking-[0.3em] uppercase text-[#F5B400]">
                Capital Ciudadano · Frente Ya
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-headings font-bold uppercase tracking-tighter text-white text-5xl sm:text-7xl lg:text-8xl xl:text-[8.5rem] leading-[0.92]"
              data-testid="flagship-headline"
            >
              Cumplís{" "}
              <span className="text-[#F5B400]">18</span>
              <br />y te comprás
              <br />
              <span className="fy-underline text-white">tu casa</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-8 max-w-xl text-lg sm:text-xl text-white/80 leading-relaxed"
            >
              Cada argentino va a recibir, en una <strong className="text-white">billetera virtual</strong>, el dinero para
              <strong className="text-[#F5B400]"> comprar su casa</strong>. Es un derecho por ser argentino, es un regalo, es gratis; para que con ese patrimonio base, <span className="text-[#F5B400] font-semibold">puedas arrancar a diseñar tu futuro.</span>
            </motion.p>

            {/* Pillars */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="mt-10 grid grid-cols-3 gap-3 sm:gap-4 max-w-xl"
              data-testid="flagship-pillars"
            >
              {PILLARS.map((p) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.title}
                    className="border border-white/15 bg-white/[0.04] p-4 sm:p-5"
                  >
                    <div className="w-10 h-10 border-2 border-[#F5B400] rounded-full flex items-center justify-center mb-3">
                      <Icon size={18} className="text-[#F5B400]" />
                    </div>
                    <div className="font-headings text-sm sm:text-base font-bold uppercase tracking-tight text-white leading-tight">
                      {p.title}
                    </div>
                    <div className="font-headings text-xs sm:text-sm uppercase tracking-wide text-[#F5B400]">
                      {p.subtitle}
                    </div>
                  </div>
                );
              })}
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={() => go("simulador")}
                className="group bg-[#F5B400] hover:bg-[#d99f00] text-[#0A192F] font-headings uppercase tracking-wide text-base px-8 py-4 inline-flex items-center justify-center gap-2 transition-all"
                data-testid="flagship-cta-simular"
              >
                Calculá tu Capital Ciudadano
                <ArrowDown size={18} className="group-hover:translate-y-1 transition-transform" />
              </button>
              <button
                onClick={() => go("sumate")}
                className="group border-2 border-white/40 hover:border-[#F5B400] text-white hover:text-[#F5B400] font-headings uppercase tracking-wide text-base px-8 py-4 inline-flex items-center justify-center gap-2 transition-all"
                data-testid="flagship-cta-sumate"
              >
                Sumate al movimiento
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>

            {/* Slogan */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.85 }}
              className="mt-12 pt-6 border-t border-white/10 inline-flex flex-wrap items-baseline gap-x-3"
            >
              <span className="font-headings text-sm sm:text-base uppercase tracking-[0.2em] text-white/60">
                Menos promesas.
              </span>
              <span className="font-headings text-sm sm:text-base uppercase tracking-[0.2em] text-[#F5B400]">
                Más resultados.
              </span>
            </motion.div>
          </div>

          {/* RIGHT: Flyer image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div
                aria-hidden
                className="absolute -top-4 -left-4 w-24 h-24 border-l-4 border-t-4 border-[#F5B400] z-10"
              />
              <div
                aria-hidden
                className="absolute -bottom-4 -right-4 w-24 h-24 border-r-4 border-b-4 border-[#2563EB] z-10"
              />
              <img
                src={FLYER_URL}
                alt="Cumplís 18 y te comprás tu casa — Frente Ya"
                className="w-full shadow-2xl shadow-black/50"
                data-testid="flagship-flyer"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Modal: Nuestro sistema (Cómo funciona el Capitalismo de Abundancia) */}
      <AnimatePresence>
        {showSystem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/85 z-[70] overflow-y-auto overscroll-contain"
            onClick={() => setShowSystem(false)}
            data-testid="flagship-sistema-modal"
          >
            <button
              onClick={() => setShowSystem(false)}
              className="fixed top-4 right-4 bg-black/70 hover:bg-black text-white hover:text-[#F5B400] rounded-full p-2 z-[72] shadow-lg"
              aria-label="Cerrar"
              data-testid="flagship-sistema-close"
            >
              <X size={26} />
            </button>
            <motion.div
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 20 }}
              transition={{ duration: 0.25 }}
              className="relative max-w-4xl w-full mx-auto my-8 px-4 pt-16 pb-8"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src="/img/nuestro-sistema.jpeg"
                alt="Cómo funciona el Capitalismo de Abundancia — infografía de Frente Ya"
                className="w-full h-auto shadow-2xl"
              />
              <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="/img/nuestro-sistema.jpeg"
                  download="Frente-Ya-Nuestro-Sistema.jpeg"
                  className="bg-[#F5B400] hover:bg-[#d99f00] text-[#0A192F] font-headings uppercase tracking-wide text-sm px-6 py-3 inline-flex items-center justify-center gap-2"
                  data-testid="flagship-sistema-download"
                >
                  Descargar infografía
                </a>
                <button
                  onClick={() => { setShowSystem(false); go("simulador"); }}
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A192F] font-headings uppercase tracking-wide text-sm px-6 py-3 inline-flex items-center justify-center gap-2 transition-colors"
                >
                  Calculá tu Capital Ciudadano
                  <ArrowDown size={14} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default FlagshipPromise;
